import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, X } from 'lucide-react';
import api from '../api';

const ProfileCompletion = ({ onClose }) => {
  const [profile, setProfile] = useState(null);
  const [completionSteps, setCompletionSteps] = useState([]);
  
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const response = await api.get(`/creators/${user.userId}/profile`);
        setProfile(response.data);
        calculateCompletion(response.data);
      } catch (error) {
        // Error handled silently
      }
    };
    
    fetchProfileData();
  }, []);

  const calculateCompletion = (profileData) => {
    const steps = [
      {
        id: 'social_media',
        title: 'Connect social media accounts',
        description: 'Add your Instagram, YouTube, or other social platforms',
        completed: profileData.instagramHandle || profileData.youtubeHandle || profileData.twitterHandle,
        action: 'Add social media',
        route: '/dashboard/creator/profile'
      },
      {
        id: 'interests',
        title: 'Select your content categories',
        description: 'Choose topics you create content about',
        completed: profileData.contentCategories && profileData.contentCategories.length > 0,
        action: 'Set categories',
        route: '/dashboard/creator/profile'
      },
      {
        id: 'avatar',
        title: 'Upload profile picture',
        description: 'Add a professional photo to your profile',
        completed: profileData.profilePictureUrl,
        action: 'Upload photo',
        route: '/dashboard/creator/profile'
      },
      {
        id: 'bio',
        title: 'Write your bio',
        description: 'Tell brands about yourself and your content',
        completed: profileData.bio && profileData.bio.length > 50,
        action: 'Write bio',
        route: '/dashboard/creator/profile'
      },
      {
        id: 'location',
        title: 'Add your location',
        description: 'Help brands find creators in their target area',
        completed: profileData.location,
        action: 'Set location',
        route: '/dashboard/creator/profile'
      },
      {
        id: 'portfolio',
        title: 'Add portfolio items',
        description: 'Showcase your best content and collaborations',
        completed: profileData.portfolioItems && profileData.portfolioItems.length > 0,
        action: 'Add portfolio',
        route: '/dashboard/creator/profile'
      }
    ];
    
    setCompletionSteps(steps);
  };

  const completedCount = completionSteps.filter(step => step.completed).length;
  const totalSteps = completionSteps.length;
  const completionPercentage = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  const handleStepClick = (route) => {
    window.location.href = route;
  };

  if (!profile) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-blue-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Complete your profile for more features and opportunities!</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Profile Completion</span>
          <span className="text-sm font-medium text-collaborb-band4">{completionPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-collaborb-band4 h-2 rounded-full transition-all duration-300"
            style={{ width: `${completionPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Completion Steps */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {completionSteps.map((step) => (
          <div 
            key={step.id}
            onClick={() => !step.completed && handleStepClick(step.route)}
            className={`p-3 rounded-lg border cursor-pointer transition-all ${
              step.completed 
                ? 'bg-green-50 border-green-200' 
                : 'bg-gray-50 border-gray-200 hover:bg-collaborb-band1 hover:border-collaborb-band3'
            }`}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-1">
                {step.completed ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <h4 className={`text-sm font-medium ${
                  step.completed ? 'text-green-800' : 'text-gray-900'
                }`}>
                  {step.title}
                </h4>
                <p className={`text-xs mt-1 ${
                  step.completed ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {step.description}
                </p>
                {!step.completed && (
                  <button className="text-xs text-blue-600 hover:text-blue-800 mt-1 font-medium">
                    {step.action} →
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {completionPercentage === 100 && (
        <div className="mt-4 p-3 bg-green-100 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm font-medium text-green-800">
              🎉 Profile Complete! You're now eligible for premium collaboration opportunities.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileCompletion;

